import { useMemo, useState } from 'react'

import {
  requestFile,
  resourceScopeTag,
  saveFile,
  useWindow,
  useQueryResult,
} from '@krp/client-runtime'
import {
  ImportFileScreen,
  ONEC_SUPPLY_WINDOWS,
  attemptKey,
  buildDownloadRequest,
  attemptRecorded,
  buildValidateArgs,
  importApi,
  makeSelectFileVm,
  newCommandAttempt,
  onecImportFile,
  onecImportReport,
  onecImportValidate,
  onecConnectionForm,
  type FileActions,
  type ImportDownload,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useResourceRevision } from '../platform/useResourceRevision'

/** Binding окна «Файл накладной»: сведения и отчёт по rid и fileId из адреса окна. */
export function ImportFileBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const fileId = window.params.fileId ?? ''
  const intent = useAppSelector((state) => state.onec.importFlow)

  const args = useMemo(() => (resourceId === '' || fileId === '' ? null : { resourceId, fileId }), [resourceId, fileId])
  const file = useQueryResult(onecImportFile, args)
  const report = useQueryResult(onecImportReport, args)
  const [validateTrigger, validate] = onecImportValidate.useMutation({ fixedCacheKey: `${resourceId}:${fileId}:validate` })

  const resourceVersion = useResourceRevision(resourceId, onecConnectionForm.kind, 'import.read')
  const selectVm = useMemo(() => makeSelectFileVm(), [])
  const built = selectVm({
    resourceId,
    fileId,
    file,
    report,
    validate,
    resourceVersion,
  })
  // Отказ скачивания — текст сервера дословно под кнопками; файл в Redux не попадает.
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const vm = useMemo(
    () => (downloadError === null ? built : { ...built, download: { errorText: downloadError } }),
    [built, downloadError],
  )

  const actions = useMemo<FileActions>(() => {
    const download = (kind: ImportDownload, fallbackName: string) => {
      setDownloadError(null)
      void dispatch(requestFile(buildDownloadRequest(kind, resourceId, fileId))).then((result) => {
        if (result.ok) saveFile(result.data, fallbackName)
        else setDownloadError(result.error.message)
      })
    }
    return {
      reload: () => dispatch(importApi.util.invalidateTags([resourceScopeTag(resourceId)])),
      validate: () => {
        if (vm.configurationVersion === null) return
        const key = attemptKey('validate', resourceId, fileId)
        const attempt = intent.attempts[key] ?? newCommandAttempt()
        dispatch(attemptRecorded({ key, attempt }))
        void validateTrigger(buildValidateArgs(resourceId, vm.configurationVersion, fileId, attempt))
      },
      downloadOriginal: () => download('original', `${fileId}.xlsx`),
      downloadReportCsv: () => download('report-csv', `${fileId}-report.csv`),
      // Окно файла сменяется «Поставкой из накладной»: файл едет в адресе окна, а не руками.
      createSupply: () => window.replaceWith(ONEC_SUPPLY_WINDOWS.fromInvoice, { rid: resourceId, fileId }),
      close: window.close,
    }
  }, [dispatch, resourceId, fileId, vm.configurationVersion, intent.attempts, validateTrigger, window])

  return <ImportFileScreen vm={vm} actions={actions} />
}
